import { useMutation, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

// Context
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { useMotivators } from "@Motivators/context/Common/MotivatorsContext";

// Third party
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

// Hooks
import { useLocation } from "@Common/hooks/Location/useLocation";

// Helpers
import { accountFormat } from "@/Common/helpers/number";
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { emptyValidation } from "@/Layouts/components/Tables/DataSmartTable/validation/TableValidations";
import { getFormattedDate } from "@dealerwave/dw-layer-date-converter";

// API
import actionsList from "@/Constants/actionsList";

// Components
import RowFeedback from "@/Common/helpers/RowFeedback";
import TableInputValue from "@/Layouts/components/InputComponents/TableInputValue";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";

// MUI
import { FormControl, TextField } from "@mui/material";
import { GridActionsCellItem, GridRowModes, useGridApiContext, useGridApiRef } from "@mui/x-data-grid-premium";

// Icons
import { SaveIconComponent } from "@/Layouts/components/Tables/DataSmartTable/additional/SaveIconComponent";
import TypographyCell from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import CancelIcon from "@mui/icons-material/Close";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

function InputEditCell(props) {
	const apiRef = useGridApiContext();
	const { id, field, error, duplicated, setDuplicated } = props;
	const [value, setValue] = useState(props?.value || "");

	const handleChange = (event) => {
		let inputValue = event.target.value;
		if (props.useUpperCase) {
			inputValue = inputValue?.toUpperCase();
		}
		// `debounceMs` is important to avoid the input to be updated too often.
		apiRef.current.setEditCellValue({ id, field, value: inputValue, debounceMs: 200 });
		setValue(inputValue);
		setDuplicated(false);
	};

	const handleKeyDown = (event) => {
		if (props.validate) {
			const validated = keyDownValidations(props.validate);
			const isValid = validated.test(event.key);
			if (!isValid && !event.key.match("Arrow")) {
				event.preventDefault();
			}
		}
	};

	const keyDownValidations = (type) => {
		let validationRegex = "";
		if (type === "NAME") {
			validationRegex = /^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÔÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆ∂ð ,.'-]+$/u;
		}
		return validationRegex;
	};

	return (
		<FormControl fullWidth>
			<TextField
				variant="outlined"
				value={value}
				inputProps={{
					minLength: props.minLength || 0,
					maxLength: props.maxLength || Infinity,
				}}
				className="text-input-table"
				onChange={handleChange}
				onKeyDown={handleKeyDown}
				error={!value || error || duplicated}
			/>
		</FormControl>
	);
}

function InputEditInputCell(amountProps) {
	const apiRef = useGridApiContext();
	const { id, field, hasFocus } = amountProps;
	const [value, setValue] = useState(amountProps.value?.value);
	const [inputType, setInputType] = useState(amountProps.value?.input || "Dollar");

	const handleChangeType = (_valueData, input) => setInputType(input);

	return (
		<TableInputValue
			isControlledApi={true}
			isEditingMode={hasFocus}
			isRequiredOnChange={true}
			data={[
				{
					type: "Dollar",
					min: 0,
					max: 10000,
					decimalsEditable: true,
					decimals: 2,
				},
				{
					type: "Percent",
					min: 0,
					max: 100,
					decimalsEditable: true,
					decimals: 2,
				},
			]}
			value={{
				value,
				input: inputType,
			}}
			defaultType={inputType}
			onChange={(updated) => setValue(updated)}
			onChangeType={handleChangeType}
			onBlur={(updatedValue, input) => {
				setValue(updatedValue);
				apiRef.current.setEditCellValue({ id, field, value: { value: updatedValue, input } });
			}}
			error={!value}
			sx={{ height: "27px" }}
		/>
	);
}

const getItemsFromResponse = (response) => response?.units || [];

const getUpdatedUnits = (currentCache) => (currentCache?.units ? [...currentCache.units] : []);

export default function SpecialUnitsTable(props) {
	const apiRef = useGridApiRef();
	const queryClient = useQueryClient();
	const { timezone } = useLocation();

	const { isLoading, locationId, mutations, motivatorType, setSelection, selection } = useMotivators();
	const user = useSelector((state) => state.user.user);
	const disableActions = !props?.dateRange?.from || !props?.dateRange?.to;

	const { listId } = props;

	const [duplicated, setDuplicated] = useState(false);

	// Constants
	const APP_THEME_HEADER = "super-app-theme--header";

	// Auth
	const { useAuth } = useAuthorization();
	const editSpecialUnits = useAuth(actionsList.EDIT_SPECIAL_UNIT_LISTS);

	// Subscriptions
	const { getSubscription } = useSubscriptions();
	const specialUnitsListSubscription = getSubscription([motivatorType + "Body", locationId, listId]);

	const handleSetSelection = (item) =>
		setSelection((prev) => ({ ...prev, lastModifiedBy: item.lastModifiedBy, lastModifiedOn: item.lastModifiedOn }));

	// Subscriptions Callbacks
	specialUnitsListSubscription?.subscriptions?.onCreateItem?.setCallback((result) => {
		const response = result.data.onCreateSpecialUnitsItem;
		const items = getItemsFromResponse(response);
		if (items.length) {
			queryClient.setQueryData(["specialUnitsSetupEntry", locationId, listId], (currentCache) => {
				const updated = getUpdatedUnits(currentCache);
				items.forEach((item) => {
					const present = updated.find((element) => element._id === item._id);
					if (present) {
						// Update
						updated.forEach((updateItem, key) => {
							if (updateItem._id === item._id) {
								updated[key] = item;
								handleSetSelection({ lastModifiedBy: item.createdBy, lastModifiedOn: new Date() });
							}
						});
					} else {
						updated.push(item); // Add new
						handleSetSelection({ lastModifiedBy: item.createdBy, lastModifiedOn: new Date() });
					}
				});
				return { ...currentCache, units: updated };
			});
		}
	});

	specialUnitsListSubscription?.subscriptions?.onDeleteItem?.setCallback((result) => {
		const response = result.data.onDeleteSpecialUnitsItem;
		queryClient.setQueryData(["specialUnitsSetupEntry", locationId, listId], (currentCache) => {
			handleSetSelection(response);
			setSelection((prev) => ({ ...prev, lastModifiedOn: new Date() }));
			return { ...currentCache, units: response?.units || [] };
		});
	});

	specialUnitsListSubscription?.subscriptions?.onUpdateSpecialUnitsList?.setCallback((result) => {
		const response = result.data.onUpdateSpecialUnitsList;
		queryClient.setQueryData(["specialUnitsModel", locationId, listId], (currentCache) => {
			props.setLoadingDateRange({ from: false, to: false });
			handleSetSelection(response);
			setSelection((prev) => ({ ...prev, lastModifiedOn: new Date() }));
			return {
				...currentCache,
				title: response.title,
				effectiveDateFrom: response.effectiveDateFrom,
				effectiveDateTo: response.effectiveDateTo,
				rules: response.rules,
			};
		});
	});

	const { mutate: addSpecialUnit } = useMutation({
		mutationFn: async (input) => {
			return mutations.createItem({
				locationId,
				listId,
				commissionCycleId: props.commissionCycleId,
				row: input,
				specialUnitsListType: selection?.specialUnitsListType,
			});
		},
	});

	const { mutate: deleteRows } = useMutation({
		mutationFn: async (rowIds) =>
			mutations.deleteItem({
				locationId,
				listId,
				itemIds: rowIds,
				commissionCycleId: props.commissionCycleId,
				specialUnitsListType: selection?.specialUnitsListType,
			}),
	});

	// Rows should be passed to an state to be able to update them without side effect.
	// Mutate the cache to update the rows will work as well.
	const [rows, setRows] = useState(props.data || []);

	// Controls the edit modes using a state.
	const [rowModesModel, setRowModesModel] = useState({});

	// Controls the selected rows using a state.
	const [selectedRows, setSelectedRows] = useState([]);

	// Controls to know if some row is loading. So the add and delete buttons can be disabled.
	const [rowLoading, setRowLoading] = useState(false);

	// This it's the correct way to listen to the edit mode changes.
	const editModeActive = useMemo(() => Object.values(rowModesModel)?.some(({ mode }) => mode === GridRowModes.Edit), [rowModesModel]);

	const handleRowEditStart = (_params, event) => {
		if (editModeActive) {
			event.defaultPrevented = true;
			event.defaultMuiPrevented = true;
		}
	};

	const handleRowEditStop = (_, event) => {
		event.defaultMuiPrevented = true;
	};

	// Thus fn is called when the user clicks on the "Save" button.
	const handleSaveClick = (id) => {
		const rowEdit = apiRef.current.state.editRows;
		const row = Object.values(rowEdit)[0];
		const keys = Object.keys(row);
		const errors = keys.filter((key) => row[key].error || !row[key].value);

		if (!errors?.length) {
			setRowModesModel((prev) => ({
				...prev,
				[id]: { mode: GridRowModes.View },
			}));
		} else if (errors?.length === keys.length) {
			apiRef.current.setState((state) => ({
				...state,
				editRows: {
					...state.editRows,
					[id]: {
						...state.editRows[id],
						...errors.reduce((acc, key) => ({ ...acc, [key]: { ...state.editRows[id][key], error: true } }), {}),
					},
				},
			}));
		}
	};

	// This fn is called when the user clicks on the "Cancel" button
	const handleCancelClick = (id) => {
		setRowModesModel({
			...rowModesModel,
			[id]: { mode: GridRowModes.View, ignoreModifications: true },
		});

		const editedRow = rows.find((row) => row._id === id);
		if (editedRow?.isNew) {
			setRows(rows.filter((row) => row._id !== id));
		}
	};

	// This fn is called when the user clicks on the "Delete" button.
	const handleDeleteClick = async (ids) => {
		setRowLoading(true);

		ids?.forEach((id) => {
			RowFeedback.skeleton(id, apiRef).delete().display();
		});

		deleteRows(ids, {
			onSuccess: () => {
				setRows((prev) => prev.filter((row) => !ids.includes(row._id)));
				setSelectedRows((prev) => prev.filter((id) => !ids.includes(id)));
			},
			onError: () => {
				ids.forEach((id) => {
					RowFeedback.skeleton(id, apiRef).errorDelete();
				});
			},
			onSettled: () => {
				setRowLoading(false);
			},
		});
	};

	// This fn is called when the user clicks on the "Add" button.
	const handleAddRow = () => {
		// Define a default row initial values.
		const key = uuidv4();
		const newRow = {
			_id: key,
			isNew: true,
			createdAt: "Now",
			createdBy: user,
			stockNumber: "",
			description: "",
			commission: {
				input: "Dollar",
				value: 0,
			},
		};

		// Add the new row to the rows state.
		setRows((prev) => [newRow, ...prev]);

		// Set the new row in edit mode.
		setRowModesModel((prev) => ({
			...prev,
			[key]: { mode: GridRowModes.Edit },
		}));
	};

	// This fn is called when the user clicks on the "Add" button.
	const handleRowSelected = (newSelection) => {
		setSelectedRows(newSelection);
	};

	// This fn is call after the row is added or edited, and it should be used to save data to the backend.
	const handleProcessRowUpdate = async (newRow, oldRow) => {
		// compare the old row with the new row to know if changes were made.
		const isModified = Object.keys(newRow).some((key) => newRow[key] !== oldRow?.[key]);
		if (!isModified) {
			return oldRow;
		}

		const updatedRow = { ...newRow, isNew: false, createdBy: newRow?.createdBy?._id };

		const input = {
			...updatedRow,
			lastModifiedOn: new Date(),
		};
		delete input.isNew;

		setRowLoading(true);

		// Here you should save the data to the backend.
		RowFeedback.skeleton(updatedRow._id, apiRef).display();

		// Mutation operations
		let result = null;
		const mutateOperation = (id, resolve) => ({
			onSuccess: (response) => {
				RowFeedback.skeleton(id, apiRef).success();
				resolve?.(response);
			},
			onError: (err) => {
				if (err?.message.includes("already listed")) {
					RowFeedback.skeleton(id, apiRef).error();
					setRowModesModel((prev) => ({
						...prev,
						[id]: { mode: GridRowModes.Edit },
					}));

					const fields = ["commission", "stockNumber", "description"];
					fields.forEach((field) => {
						apiRef.current.setEditCellValue({ id, field, value: input[field], debounceMs: 200 });
					});

					setDuplicated(true);
				}
			},
			onSettled: () => {
				setRowLoading(false);
			},
		});

		if (newRow?.isNew) {
			delete input._id;
			input.createdBy = user._id;
			input.createdAt = moment().utc();

			result = new Promise((resolve) => {
				addSpecialUnit(input, mutateOperation(updatedRow._id, resolve));
			});
		} else {
			result = new Promise((resolve) => {
				addSpecialUnit(input, mutateOperation(input._id, resolve));
			});
		}

		if (result) {
			await result;
		}
		return updatedRow;
	};

	// This fn is call if an error was thrown when sending data to the backend.
	const handleProcessRowUpdateError = (error) => {
		// Here you should handle the error.
		console.error(error);
	};

	// This fn is called when the mode of any row is changed. Either to "edit" or "view".
	const handleRowModesModelChange = (newModel) => {
		setRowModesModel(newModel);
	};

	const columns = [
		{ field: "_id", headerName: "ID", width: 50, hide: true, filterable: false },
		{
			field: "createdAt",
			headerName: "Entry Date",
			type: "date",
			width: 180,
			headerClassName: APP_THEME_HEADER,
			valueFormatter: (value) => (value === "Now" ? "Now" : getFormattedDate(value, timezone, DATE_FORMAT.fullAbbrTime12hPadded)),
		},
		{
			field: "createdBy",
			headerName: "Added By",
			width: 180,
			headerClassName: APP_THEME_HEADER,
			valueFormatter: (value) => {
				if (value?.fullName) {
					return value.fullName;
				}
				return "";
			},
		},
		{
			field: "stockNumber",
			headerName: "Stock Number",
			headerClassName: APP_THEME_HEADER,
			width: 160,
			editable: editSpecialUnits,
			renderEditCell: (params) => (
				<InputEditCell {...params} useUpperCase minLength={4} maxLength={8} duplicated={duplicated} setDuplicated={setDuplicated} />
			),
			preProcessEditCellProps: (params) => {
				const valueExists = emptyValidation(params.props.value, { minLength: 4, maxLength: 8 });
				return { ...params.props, error: !valueExists };
			},
		},
		{
			field: "description",
			headerName: "Description",
			headerClassName: APP_THEME_HEADER,
			width: 220,
			editable: editSpecialUnits,
			cellClassName: "table-noWrap",
			renderEditCell: (params) => <InputEditCell {...params} />,
			preProcessEditCellProps: (params) => {
				const valueExists = emptyValidation(params.props.value);
				return { ...params.props, error: !valueExists };
			},
			renderCell: (params) => {
				return (
					<TypographyCell fontSize="14.5px" params={params}>
						{params.value}
					</TypographyCell>
				);
			},
		},
		{
			field: "status",
			headerName: "Status",
			headerClassName: APP_THEME_HEADER,
			width: 90,
			editable: false,
			cellClassName: "table-noWrap",
			valueGetter: (value, row) => {
				const { commissionCycleStartDate: start, commissionCycleEndDate: end } = props.selectedItem ?? {};

				if (!row?.saleDate || !start || !end) {
					return "";
				}

				const inCycle = moment.utc(row.saleDate).isBetween(start, end, "day", "[]");
				return inCycle ? value : "";
			},

			renderCell: (params) => {
				return (
					<TypographyCell fontSize="14.5px" params={params}>
						{params.value}
					</TypographyCell>
				);
			},
		},
		{
			field: "commission",
			headerName: "Commission",
			headerClassName: `super-app-theme--header`,
			width: 129,
			align: "right",
			editable: editSpecialUnits,
			renderEditCell: (params) => <InputEditInputCell {...params} />,
			preProcessEditCellProps: (params) => {
				const valueExists = emptyValidation(params.props.value.value);
				return { ...params.props, error: !valueExists };
			},
			cellClassName: (params) => {
				if (params?.cellMode === "view") {
					return clsx({
						"cell-with-dollar-symbol": params.value?.input === "Dollar",
						"cell-with-percent-symbol": params.value?.input === "Percent",
					});
				}
				return null;
			},
			valueFormatter: (value) => accountFormat(value.value),
		},
		{
			field: "actions",
			type: "actions",
			width: 95,
			getActions: ({ id }) => {
				const isInEditMode = rowModesModel?.[id]?.mode === "edit";
				if (isInEditMode) {
					return [
						<GridActionsCellItem
							key="saveSpecial"
							icon={<SaveIconComponent />}
							label="Save"
							onClick={() => handleSaveClick(id)}
						/>,
						<GridActionsCellItem
							key="cancel"
							icon={<CancelIcon />}
							label="Cancel"
							className="textPrimary"
							onClick={() => handleCancelClick(id)}
							color="inherit"
						/>,
					];
				}

				return [];
			},
		},
	];

	const isComponentLoading = isLoading || rowLoading;

	useEffect(() => {
		setRows(props.data || []);
		return () => {
			setRows([]);
			setDuplicated(false);
		};
	}, [props.data]);

	const customPropsAddons = !disableActions
		? {
				tableHidedProps: {
					currentMonth: true,
					sectionFirstTitle: "No Special Units in this list.",
					onAddTheFirstOne: !disableActions && handleAddRow,
				},
		  }
		: null;

	return (
		<DataSmartTable
			columns={columns}
			rows={rows}
			rowHeight={28}
			apiRef={apiRef}
			editMode="row"
			maxRows={11}
			enableCustomScrollbars
			disableColumnMenu
			disableRowSelectionOnClick
			checkboxSelection
			isRowSelectable={() => !editModeActive && editSpecialUnits}
			onRowSelectionModelChange={handleRowSelected}
			rowModesModel={rowModesModel}
			processRowUpdate={handleProcessRowUpdate}
			onProcessRowUpdateError={handleProcessRowUpdateError}
			onRowModesModelChange={handleRowModesModelChange}
			onRowEditStart={handleRowEditStart}
			onRowEditStop={handleRowEditStop}
			columnVisibilityModel={{
				_id: false,
			}}
			slots={{
				toolbar: CommonToolbar,
				noRowsOverlay: NoRowsOverlay,
			}}
			slotProps={{
				toolbar: {
					filter: {
						hide: !!selectedRows.length,
						disabled: editModeActive,
					},
					add: {
						onAddAction: handleAddRow,
						hide: !!selectedRows.length,
						disabled: editModeActive || !editSpecialUnits || isComponentLoading,
					},
					delete: {
						onDeleteAction: handleDeleteClick,
						deleteTitle: "Remove Units",
						hide: !selectedRows.length,
						disabled: !editSpecialUnits || isComponentLoading,
					},
				},
				noRowsOverlay: {
					title: "No Special Units Data",
				},
			}}
			hideFooter
			customProps={{
				...customPropsAddons,
				tableClassName: {
					"functional-table": true,
					"edit-table": true,
				},
				expandableRows: false,
				hide: !rows?.length,
			}}
		/>
	);
}

InputEditCell.propTypes = {
	id: PropTypes.string,
	value: PropTypes.string,
	field: PropTypes.string,
	error: PropTypes.bool,
	minLength: PropTypes.number,
	maxLength: PropTypes.number,
	useUpperCase: PropTypes.bool,
	validate: PropTypes.func,
	duplicated: PropTypes.bool,
	setDuplicated: PropTypes.func,
};

SpecialUnitsTable.propTypes = {
	dateRange: PropTypes.shape({
		from: PropTypes.string,
		to: PropTypes.string,
	}),
	user: PropTypes.shape({
		_id: PropTypes.string,
		fullName: PropTypes.string,
	}),
	editSpecialUnits: PropTypes.bool,
	addSpecialUnit: PropTypes.func,
	deleteRows: PropTypes.func,
	setRows: PropTypes.func,
	setSelectedRows: PropTypes.func,
	setRowModesModel: PropTypes.func,
	enqueueSnackbar: PropTypes.func,
	apiRef: PropTypes.object,
	RowFeedback: PropTypes.object,
	SnackbarsContent: PropTypes.func,
	SnackbarsActions: PropTypes.func,
	InputEditCell: PropTypes.func,
	InputEditInputCell: PropTypes.func,
	SaveIcon: PropTypes.elementType,
	CancelIcon: PropTypes.elementType,
	getFormatDate: PropTypes.func,
	emptyValidation: PropTypes.func,
	accountFormat: PropTypes.func,
	uuidv4: PropTypes.func,
	moment: PropTypes.func,
	listId: PropTypes.string,
	locationId: PropTypes.string,
	commissionCycleId: PropTypes.string,
	data: PropTypes.arrayOf(PropTypes.object),
	setLoadingDateRange: PropTypes.func,
	selectedItem: PropTypes.object,
};
